import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { showToast } from "ui/components/common";
import { Skeleton, Table, Modal } from "antd";
import {
  getLicenseOrdersDetail,
  refundOrder,
} from "pages/dashboard/license/service";
import { OrderInfo, OrderItemType } from "lib/types";
import type { TableColumnsType } from "antd";
import { getCurrencySymbol } from "lib/utils";
import "../../page.scss";

const columns: TableColumnsType<OrderItemType> = [
  {
    title: "Item",
    dataIndex: "itemName",
    className: "font-semibold",
  },
  {
    title: "Camera Name",
    dataIndex: "cameraName",
  },
  {
    title: "Customer Name",
    dataIndex: "customerName",
  },
  {
    title: "Site Name",
    dataIndex: "siteName",
  },
  // {
  //   title: "SKU",
  //   dataIndex: "sku",
  // },
  {
    title: "Storage",
    dataIndex: "storageInGb",
    render: (storageInGb: string) => {
      return <span>{storageInGb} GB</span>;
    },
  },
  {
    title: "AI Credit",
    dataIndex: "aiCredit",
    render: (aiCredit: string) => {
      return <span>{parseInt(aiCredit).toLocaleString() || "-"}</span>;
    },
  },
  {
    title: "Monitoring",
    render: (e) => {
      return (
        <span>{e?.licenseCapability?.containsMonitoring ? "On" : "Off"}</span>
      );
    },
  },
  {
    title: "Price",
    dataIndex: "price",
    render: (price: number) => {
      return (
        <span>{getCurrencySymbol("") + (price / 100)?.toLocaleString()}</span>
      );
    },
  },
];

export default function Page() {
  const params = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [ordersDetail, setOrdersDetail] = useState<OrderInfo>();
  const orderId = useMemo(() => {
    return Number(params.id);
  }, [params]);

  useEffect(() => {
    queryDetail();
  }, [orderId]);

  const queryDetail = async () => {
    setIsLoading(true);
    const response = await getLicenseOrdersDetail(orderId);
    setIsLoading(false);
    if (response.status !== 200) {
      showToast(1, "Failed to get orders detail. Please try again later.");
      return;
    }

    setOrdersDetail(response.data?.data as OrderInfo);
  };

  const paymentStatus = (e: OrderInfo) => {
    if (e.isRefunded) {
      return <p className="text-[#FF9800] font-semibold">Refund</p>;
    }
    switch (e.status) {
      case "confirmed":
      case "paid":
        return <p className="text-[#179954] font-semibold">Paid</p>;

      default:
        return <p className="text-[#717171] font-semibold">Unpaid</p>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center w-full h-full px-12 pt-16">
        <Skeleton />
      </div>
    );
  }

  return (
    <div className="flex justify-center w-full h-full p-6">
      <div className="bg-[#FAFAFA] rounded-2xl w-full h-full p-10 relative min-w-[500px]">
        <div className="font-semibold">
          Invoice for Order {ordersDetail?.no || "-"}
        </div>
        <div className="flex mt-8 gap-4">
          <div className="max-w-[50%] min-w-32">
            <p className="font-semibold">Bill To</p>
            <p className="break-normal line-clamp-3 overflow-hidden">
              {ordersDetail?.billTo}
            </p>
          </div>
          <div className="flex-grow overflow-hidden">
            <p className="font-semibold">
              <span className="w-32 inline-block"> Invoice #</span>
              <span className="font-normal">
                {ordersDetail?.invoiceNo || "-"}
              </span>
            </p>
            <p className="font-semibold">
              <span className="w-32 inline-block"> Invoice Date</span>

              <span className="font-normal">
                {ordersDetail?.invoiceDate || "-"}
              </span>
            </p>
            <p className="font-semibold">
              <span className="w-32 inline-block"> Due Date</span>

              <span className="font-normal">
                {ordersDetail?.invoiceDueDate || "-"}
              </span>
            </p>
          </div>
        </div>
        <Table
          className="order-detail-table w-full mt-8"
          scroll={{ y: "calc(100vh - 550px)" }}
          columns={columns}
          dataSource={ordersDetail?.orderItems}
          pagination={false}
          rowClassName={() => "no-row-border"}
        />
        <p className="flex-grow text-right pr-[22px] font-semibold mt-2">
          <span className="mr-3">Total</span>{" "}
          {getCurrencySymbol(ordersDetail.currency)}{" "}
          {(ordersDetail?.amount / 100)?.toLocaleString()}
        </p>
        {!ordersDetail?.isRefunded && ordersDetail?.status === "confirmed" && (
          <p className="text-right pr-[22px] font-semibold mt-2">
            <span className="mr-3">Refundable Amount</span>{" "}
            {getCurrencySymbol(ordersDetail.currency)}{" "}
            {(ordersDetail?.refundAmount / 100)?.toLocaleString()}
          </p>
        )}

        <div className="mt-2 w-full flex justify-between">
          <div>
            <p className="font-semibold">Payment Method</p>
            <div className="flex items-center mt-2">
              {ordersDetail?.paymentMethod === "card" && (
                <span>Credit &nbsp;</span>
              )}
              <span>
                {ordersDetail?.paymentMethod} &nbsp; {ordersDetail?.cardNo}
              </span>
              <span className="mr-4"></span>
              {paymentStatus(ordersDetail)}
            </div>
          </div>
          {!ordersDetail.isRefunded &&
            ordersDetail?.status === "confirmed" &&
            Number(ordersDetail?.refundAmount) != 0 && (
              <button
                className="rounded-2xl bg-white px-6 py-4 border border-blue-500 hover:border-blue-400 text-sm  transition-colors text-blue-500 hover:text-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
                onClick={() => {
                  Modal.confirm({
                    centered: true,
                    title: "Are you sure you want to refund this order?",
                    content: (
                      <p>
                        The refund amount is
                        <span className="font-semibold mx-1">
                          {getCurrencySymbol(ordersDetail.currency)}
                          {(ordersDetail?.refundAmount / 100)?.toLocaleString()}
                        </span>
                        . The amount will be refunded via the original payment
                        method and is expected to arrive within
                        <span className="font-semibold mx-1">5-7</span>
                        business days.
                      </p>
                    ),
                    async onOk() {
                      refundOrder(orderId).then((data) => {
                        if (data.status === 200) {
                          showToast(0, "Refund order successfully.");
                          window.location.reload();
                        }
                      });
                    },
                    icon: null,
                    okText: "Confirm",
                    okButtonProps: {
                      className: "primaryButton ",
                    },
                    cancelButtonProps: {
                      className: "primaryButton bordered",
                    },
                  });
                }}
              >
                Refund
              </button>
            )}
        </div>
      </div>
    </div>
  );
}
